import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import SchemeModal from "../components/schemeModal/SchemeModal";
import PdfSchemeModal from "../components/schemePdfModal/PdfSchemeModal";
import apiFetch from "../utils/apiFetch";
import {
  DASH,
  EQUIPMENT_TYPE_ORDER,
  displayValue,
  formatDate,
  getAllEquipmentGroups,
  getEquipmentTitle,
  getEquipmentTotal,
  getEquipmentUrl,
  getFieldRows,
  getMaintenanceItems,
  sortByDateDesc,
} from "../utils/equipmentPassportConfig";

const DIRECTUS_URL =
  process.env.REACT_APP_DIRECTUS_URL || "https://map.uzbekistonmet.uz";

const getMediaId = (scheme) =>
  typeof scheme?.media === "object" ? scheme.media?.id : scheme?.media;

const getSchemeTitle = (scheme, index) =>
  String(scheme?.title || "").trim() || `Схема ${index + 1}`;

const getPdfFileName = (title) =>
  title.toLowerCase().endsWith(".pdf") ? title : `${title}.pdf`;

const isPdfScheme = (scheme) => scheme?.mime_type === "application/pdf";

function PdfFileIcon() {
  return (
    <svg className="passport-scheme__pdf-icon" viewBox="0 0 72 88" aria-hidden="true">
      <path d="M13 2h31l15 15v67H13z" />
      <path d="M44 2v17h15" />
      <rect x="5" y="48" width="62" height="25" rx="6" />
      <text x="36" y="65" textAnchor="middle">PDF</text>
    </svg>
  );
}

function PassportState({ title, text, tone = "muted" }) {
  return (
    <Layout>
      <div className="container">
        <div className={`passport-state passport-state--${tone}`}>
          <h1>{title}</h1>
          {text && <p>{text}</p>}
        </div>
      </div>
    </Layout>
  );
}

function FieldGrid({ fields, compact = false }) {
  return (
    <dl className={`passport-field-grid${compact ? " passport-field-grid--compact" : ""}`}>
      {fields.map((field) => (
        <div className="passport-field" key={field.key || field.label}>
          <dt>{field.label}</dt>
          <dd>{displayValue(field.value)}</dd>
        </div>
      ))}
    </dl>
  );
}

function Timeline({ items, emptyText, type = "maintenance" }) {
  if (!items.length) {
    return <div className="passport-empty">{emptyText}</div>;
  }

  return (
    <div className="passport-timeline">
      {items.map((item, index) => {
        const author = item.author_user
          ? `${item.author_user.lastname || ""} ${item.author_user.name || ""}`.trim()
          : null;
        const date = item.event_date || item.date || item.event_datetime || item.date_created;

        return (
          <article className="passport-timeline__item" key={item.id || index}>
            <div className="passport-timeline__rail" />
            <div className="passport-timeline__body">
              <div className="passport-timeline__top">
                <span className={`passport-chip passport-chip--${type}`}>
                  {formatDate(date)}
                </span>
                {author && <span className="passport-timeline__author">{author}</span>}
              </div>
              <h3>{item.topic || item.protection_type || item.result || "Ёзув"}</h3>
              {item.repair_date && <p>Ремонт вақти: {formatDate(item.repair_date)}</p>}
              {item.section && <p>Участка: {item.section}</p>}
              {item.recorder_reading && <p>Қайд этувчи мослама: {item.recorder_reading}</p>}
              {item.cause && <p>Сабаб: {item.cause}</p>}
              {item.measures && <p>Чоралар: {item.measures}</p>}
              {item.comment && <p>{item.comment}</p>}
              {item.result && <p>Натижа: {item.result}</p>}
            </div>
          </article>
        );
      })}
    </div>
  );
}

function SchemeGallery({ items, onOpenImage, onOpenPdf }) {
  if (!items.length) {
    return <div className="passport-empty">Схемалар бўйича маълумот йўқ.</div>;
  }

  return (
    <div className="passport-scheme-grid">
      {items.map((item) => {
        if (item.kind === "pdf") {
          return (
            <article className="passport-scheme passport-scheme--pdf" key={item.id}>
              <div className="passport-scheme__pdf-preview">
                <PdfFileIcon />
                <span className="passport-scheme__pdf-badge">PDF схема</span>
              </div>
              <div className="passport-scheme__title" title={item.title}>{item.title}</div>
              <div className="passport-scheme__actions">
                <button
                  type="button"
                  className="passport-scheme__action passport-scheme__action--view"
                  onClick={() => onOpenPdf(item.galleryIndex)}
                >
                  Кўриш
                </button>
                <a
                  className="passport-scheme__action"
                  href={item.downloadUrl}
                  download={getPdfFileName(item.title)}
                >
                  Юклаб олиш
                </a>
              </div>
            </article>
          );
        }

        return (
          <button
            className="passport-scheme"
            type="button"
            key={item.id}
            onClick={() => onOpenImage(item.galleryIndex)}
          >
            <img src={item.thumb} alt={item.title} loading="lazy" decoding="async" />
            <span className="passport-scheme__title" title={item.title}>{item.title}</span>
          </button>
        );
      })}
    </div>
  );
}

function EquipmentCard({ type, cfg, item }) {
  const maintenance = getMaintenanceItems(item, cfg);
  const fields = getFieldRows(item, cfg).slice(0, 5);
  const title = getEquipmentTitle(item, cfg);

  return (
    <article className="passport-equipment-card">
      <div className="passport-equipment-card__top">
        <div>
          <span className="passport-chip">{cfg.label}</span>
          <h3>{title}</h3>
        </div>
        {item.id ? (
          <Link className="passport-action" to={getEquipmentUrl(type, item.id)}>
            Паспорт
          </Link>
        ) : (
          <span className="passport-action passport-action--disabled">Паспорт</span>
        )}
      </div>

      <FieldGrid fields={fields} compact />

      <div className="passport-equipment-card__meta">
        <span>Таъмир журналлари: {maintenance.length}</span>
        {/* <span>Авариялар: {accidents.length}</span> */}
      </div>
    </article>
  );
}

function EquipmentSection({ group }) {
  return (
    <section className="passport-equipment-section">
      <div className="passport-card__head">
        <span className="passport-section-number">
          {String(EQUIPMENT_TYPE_ORDER.indexOf(group.type) + 1).padStart(2, "0")}
        </span>
        <div>
          <h2>{group.cfg.pluralLabel}</h2>
          <p>{group.items.length} та бириктирилган ускуна.</p>
        </div>
      </div>

      {group.items.length ? (
        <div className="passport-equipment-grid">
          {group.items.map((item) => (
            <EquipmentCard
              key={`${group.type}-${item.id || getEquipmentTitle(item, group.cfg)}`}
              type={group.type}
              cfg={group.cfg}
              item={item}
            />
          ))}
        </div>
      ) : (
        <div className="passport-empty">Бу тур бўйича маълумот киритилмаган.</div>
      )}
    </section>
  );
}

const SubPassport = () => {
  const { slug } = useParams();
  const [substation, setSubstation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeEquipmentType, setActiveEquipmentType] = useState("transformers");
  const [activeSidePanel, setActiveSidePanel] = useState("schemes");
  const [schemeOpen, setSchemeOpen] = useState(false);
  const [schemeIndex, setSchemeIndex] = useState(0);
  const [pdfSchemeOpen, setPdfSchemeOpen] = useState(false);
  const [pdfSchemeIndex, setPdfSchemeIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const fetchSubstation = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiFetch(`/api/substation/${slug}`, { cache: "no-store" });

        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || "Подстанция не найдена");
        }

        const data = await res.json();
        if (!cancelled) setSubstation(data);
      } catch (e) {
        if (!cancelled) {
          setSubstation(null);
          setError(e.message || "Ошибка загрузки подстанции");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (slug) fetchSubstation();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const view = useMemo(() => {
    if (!substation) return null;

    const sourceSchemes = Array.isArray(substation.substation_schemes)
      ? substation.substation_schemes
      : [];
    const schemeItems = [];
    const imageSchemeItems = [];
    const pdfSchemeItems = [];

    sourceSchemes.forEach((scheme, index) => {
      const mediaId = getMediaId(scheme);
      if (!mediaId) return;

      const id = scheme.id || mediaId;
      const title = getSchemeTitle(scheme, index);

      if (isPdfScheme(scheme)) {
        const url = `${DIRECTUS_URL}/assets/${mediaId}`;
        const item = {
          id,
          title,
          url,
          downloadUrl: `${DIRECTUS_URL}/assets/${mediaId}/${encodeURIComponent(getPdfFileName(title))}?download`,
        };
        const galleryIndex = pdfSchemeItems.length;
        pdfSchemeItems.push(item);
        schemeItems.push({ ...item, kind: "pdf", galleryIndex });
        return;
      }

      const item = {
        id,
        title,
        thumb: `${DIRECTUS_URL}/assets/${mediaId}?key=thumb-480`,
        full: `${DIRECTUS_URL}/assets/${mediaId}`,
      };
      const galleryIndex = imageSchemeItems.length;
      imageSchemeItems.push(item);
      schemeItems.push({ ...item, kind: "image", galleryIndex });
    });

    const equipmentGroups = getAllEquipmentGroups(substation);
    const visibleGroups = equipmentGroups.filter((group) => group.type === activeEquipmentType);
    const accidents = sortByDateDesc(substation.substation_accidents);

    return {
      schemeItems,
      imageSchemeItems,
      pdfSchemeItems,
      equipmentGroups,
      visibleGroups,
      accidents,
      equipmentTotal: getEquipmentTotal(substation),
      baseFields: [
        { key: "city", label: "Балансда сақловчи МЭТ филиали", value: substation.city?.name },
        { key: "address", label: "ПС жойлашган ҳудуд", value: substation.address },
        { key: "voltage", label: "Кучланиши", value: substation.voltage },
        { key: "start_year", label: "Ишга туширилган йили", value: substation.start_year },
        { key: "power_mva", label: "Қуввати", value: substation.power_mva },
        { key: "longitude", label: "Узунлик координатаси", value: substation.longitude },
        { key: "latitude", label: "Кенглик координатаси", value: substation.latitude },
      ],
    };
  }, [activeEquipmentType, substation]);

  if (loading) {
    return <PassportState title="Подстанция паспорти юкланмоқда" text="Маълумотлар тайёрланмоқда." />;
  }

  if (error || !substation || !view) {
    return (
      <PassportState
        title="Подстанция топилмади"
        text={error || "URL параметрларини текширинг."}
        tone="error"
      />
    );
  }

  return (
    <Layout>
      <div className="container">
        <main className="passport-page passport-page--substation">
          <section className="passport-hero">
            <div className="passport-breadcrumbs">
              <Link to="/">Бош саҳифа</Link>
              <span>/</span>
              <Link to="/indicators/substations">Подстанциялар</Link>
              <span>/</span>
              <span>{substation.name}</span>
            </div>

            <div className="passport-hero__grid">
              <div>
                <span className="passport-kicker">Подстанция паспорти</span>
                <h1>{substation.name || "Подстанция"}</h1>
                <p>
                  {substation.city?.name || DASH} филиали, {displayValue(substation.voltage)}.
                </p>
              </div>

              <aside className="passport-identity">
                <span className="passport-identity__label">Ускуналар</span>
                <strong>{view.equipmentTotal}</strong>
                <span>{view.schemeItems.length} схема</span>
              </aside>
            </div>
          </section>

          <section className="passport-metrics">
            <div className="passport-metric">
              <span>Филиал</span>
              <strong>{substation.city?.name || DASH}</strong>
            </div>
            <div className="passport-metric">
              <span>Кучланиш</span>
              <strong>{displayValue(substation.voltage)}</strong>
            </div>
            <div className="passport-metric">
              <span>Қувват</span>
              <strong>{displayValue(substation.power_mva)}</strong>
            </div>
            <div className="passport-metric">
              <span>Авариялар</span>
              <strong>{view.accidents.length}</strong>
            </div>
          </section>

          <div className="passport-layout">
            <div className="passport-main">
              <section className="passport-card">
                <div className="passport-card__head">
                  <span className="passport-section-number">01</span>
                  <div>
                    <h2>Умумий маълумотлар</h2>
                    <p>Подстанциянинг асосий техник ва филиал маълумотлари.</p>
                  </div>
                </div>
                <FieldGrid fields={view.baseFields} />
              </section>

              <section className="passport-card passport-card--equipment-index">
                <div className="passport-card__head">
                  <span className="passport-section-number">02</span>
                  <div>
                    <h2>Ускуналар</h2>
                    <p>Ҳар бир ускуна паспорти алоҳида саҳифада очилади.</p>
                  </div>
                </div>

                <div className="passport-filter-bar">
                  {view.equipmentGroups.map((group) => (
                    <button
                      type="button"
                      key={group.type}
                      className={`passport-filter${activeEquipmentType === group.type ? " passport-filter--active" : ""}`}
                      onClick={() => setActiveEquipmentType(group.type)}
                    >
                      {group.cfg.label}
                      <span>{group.items.length}</span>
                    </button>
                  ))}
                </div>
              </section>

              {view.visibleGroups.map((group) => (
                <EquipmentSection group={group} key={group.type} />
              ))}
            </div>

            <aside className="passport-side">
              <section className="passport-card passport-card--sticky">
                <div className="passport-card__head passport-card__head--compact">
                  <span className="passport-section-number">03</span>
                  <div>
                    <h2>Схема ва журналлар</h2>
                    <p>Подстанцияга боғланган схемалар ва авария журналлари.</p>
                  </div>
                </div>

                <div className="passport-segment">
                  <button
                    type="button"
                    className={activeSidePanel === "schemes" ? "passport-segment__btn passport-segment__btn--active" : "passport-segment__btn"}
                    onClick={() => setActiveSidePanel("schemes")}
                  >
                    Схемалар
                  </button>
                  <button
                    type="button"
                    className={activeSidePanel === "accidents" ? "passport-segment__btn passport-segment__btn--active" : "passport-segment__btn"}
                    onClick={() => setActiveSidePanel("accidents")}
                  >
                    Авариялар
                  </button>
                </div>

                {activeSidePanel === "schemes" && (
                  <SchemeGallery
                    items={view.schemeItems}
                    onOpenImage={(index) => {
                      setSchemeIndex(index);
                      setSchemeOpen(true);
                    }}
                    onOpenPdf={(index) => {
                      setPdfSchemeIndex(index);
                      setPdfSchemeOpen(true);
                    }}
                  />
                )}

                {activeSidePanel === "accidents" && (
                  <Timeline
                    items={view.accidents}
                    emptyText="Авариялар бўйича маълумот йўқ."
                    type="accident"
                  />
                )}
              </section>
            </aside>
          </div>
        </main>
      </div>

      <SchemeModal
        open={schemeOpen}
        items={view.imageSchemeItems}
        index={schemeIndex}
        onClose={() => setSchemeOpen(false)}
        onChangeIndex={setSchemeIndex}
      />

      <PdfSchemeModal
        open={pdfSchemeOpen}
        items={view.pdfSchemeItems}
        index={pdfSchemeIndex}
        onClose={() => setPdfSchemeOpen(false)}
        onChangeIndex={setPdfSchemeIndex}
      />
    </Layout>
  );
};

export default SubPassport;
